import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment, useMinimalsTheme } from '@tom-ui/ui'
import { notAllowPlusMinusPointSign, onlyAllowPositiveInt } from '@tom-ui/utils'
import { BerthResponse, PierResponse } from '@yard/app/api'
import { FormProps } from '@yard/app/models/form-props.model'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { IBerthFormData } from '../../models/yard-setup.model'
import { BerthFormStore } from '../../stores/nodes/berth.form-store'
import { BerthBollardsForm } from './BerthBollardsForm'

interface Props extends FormProps {
  berth?: BerthResponse
  piers: PierResponse[]
  onSubmit: (data: IBerthFormData) => void
}

export const BerthForm = observer(({ id, berth, piers, onSubmit }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const formStore = useMemo(() => new BerthFormStore(berth), [berth])

  const handleSubmit = () => {
    if (formStore.validate()) {
      onSubmit(formStore.data)
    }
  }

  return (
    <Grid
      id={id}
      component='form'
      autoComplete='off'
      noValidate
      container
      onSubmit={event => {
        event.preventDefault()
        handleSubmit()
      }}
      spacing={theme.customSpacing.m}
    >
      <Grid size={12}>
        <TextField {...formStore.name.props} fullWidth label={t('name', 'Name')} required />
        {formStore.name.error && (
          <Typography variant='caption' color='error'>
            {t(formStore.name.error)}
          </Typography>
        )}
      </Grid>

      <Grid size={6}>
        <TextField
          {...formStore.quayMeterMarkFrom.props}
          fullWidth
          label={t('quayMeterMarkFrom', 'Quay meter mark from')}
          onKeyDown={notAllowPlusMinusPointSign}
          onChange={e => {
            onlyAllowPositiveInt(e)
            formStore.quayMeterMarkFrom.onChange(e)
          }}
          required
          type='number'
          name='quayMeterMarkFrom'
          slotProps={{
            input: {
              endAdornment: (
                <CustomInputAdornment position='end' color='secondary' padding={'24px'}>
                  {t('meters', 'Meters')}
                </CustomInputAdornment>
              ),
            },
          }}
        />
        {formStore.quayMeterMarkFrom.error && (
          <Typography variant='caption' color='error'>
            {t(formStore.quayMeterMarkFrom.error)}
          </Typography>
        )}
      </Grid>

      <Grid size={6}>
        <TextField
          {...formStore.quayMeterMarkTo.props}
          fullWidth
          label={t('quayMeterMarkTo', 'Quay meter mark to')}
          required
          type='number'
          onKeyDown={notAllowPlusMinusPointSign}
          onChange={e => {
            onlyAllowPositiveInt(e)
            formStore.quayMeterMarkTo.onChange(e)
          }}
          name='quayMeterMarkTo'
          slotProps={{
            input: {
              endAdornment: (
                <CustomInputAdornment position='end' color='secondary' padding={'24px'}>
                  {t('meters', 'Meters')}
                </CustomInputAdornment>
              ),
            },
          }}
        />
        {formStore.quayMeterMarkTo.error && (
          <Typography variant='caption' color='error'>
            {t(formStore.quayMeterMarkTo.error)}
          </Typography>
        )}
      </Grid>

      <Grid size={6}>
        <TextField
          {...formStore.maxDraft.props}
          fullWidth
          label={t('maxDraft', 'Max draft')}
          onKeyDown={notAllowPlusMinusPointSign}
          onChange={e => {
            onlyAllowPositiveInt(e)
            formStore.maxDraft.onChange(e)
          }}
          required
          type='number'
          name='maxDraft'
          slotProps={{
            input: {
              endAdornment: (
                <CustomInputAdornment position='end' color='secondary' padding={'24px'}>
                  {t('meters', 'Meters')}
                </CustomInputAdornment>
              ),
            },
          }}
        />
        {formStore.maxDraft.error && (
          <Typography variant='caption' color='error'>
            {t(formStore.maxDraft.error)}
          </Typography>
        )}
      </Grid>

      <Grid size={6}>
        <FormControl fullWidth required>
          <InputLabel error={!!formStore.pierId.error}>{t('pier', 'Pier')}</InputLabel>
          <Select {...formStore.pierId.props} label={t('pier', 'Pier')} name='pierId'>
            {piers.map(pier => (
              <MenuItem key={`pier_${pier.id}`} value={pier.id}>
                {pier.name}
              </MenuItem>
            ))}
          </Select>
          {formStore.pierId.error && (
            <Typography variant='caption' color='error'>
              {t(formStore.pierId.error)}
            </Typography>
          )}
        </FormControl>
      </Grid>

      <Grid size={12}>
        <BerthBollardsForm formStore={formStore} />
      </Grid>
    </Grid>
  )
})
